import React, { useEffect, useRef, forwardRef, useImperativeHandle, useState, useMemo } from 'react';
import { connect } from 'react-redux';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import Autocomplete from '@material-ui/lab/Autocomplete';
import Grid from '@material-ui/core/Grid';
import CircularProgress from '@material-ui/core/CircularProgress';
import Axios from 'axios';
import { apiATC } from '../../../config';
import { getListeJoaillier } from '../../../actions';

const JoaillierEnvoiForm = forwardRef(({ listeJoaillier, getListeJoaillier, onSubmitForm, dataForm, onOfChange, isOfOnly = false, isFormOnly = false, selectedOf = null, hideSubmitButton = false, onValidityChange, onHistoriqueFetched }, ref) => {
    const joaillierRef = useRef(null);
    const ofRef = useRef(null);
    const poidsRef = useRef(null);
    const [joaillierInput, setJoaillierInput] = useState('');
    const [ofInputValue, setOfInputValue] = useState('');
    const [loadingOf, setLoadingOf] = useState(false);
    const [ofError, setOfError] = useState(null); // Erreur validation OF
    const [hasSubmitted, setHasSubmitted] = useState(false);

    const filteredJoailliers = useMemo(
        () => listeJoaillier
            .filter(r => r.actif === 1)
            .filter(r => r.label?.toLowerCase().includes(joaillierInput.toLowerCase())),
        [listeJoaillier, joaillierInput]
    );

    // Fonction pour récupérer les détails du mouvement via l'API
    const fetchMovementDetails = async (numSachet, isAtelierMode = true) => {
        try {
            setLoadingOf(true);
            const action = isAtelierMode ? 'detail_atelier' : 'detail_serti';
            const response = await Axios.post(`${apiATC}/atelier/mouvements_atelier.php`, { of: numSachet, action });
            const resultData = response.data;
            
            if (resultData && Array.isArray(resultData)) {
                // Récupérer ref et refDesc du OF (depuis le premier élément ou valeurs par défaut)
                const ref_cv = resultData[0]?.ref_cv || '';
                const ref = resultData[0]?.ref || '';
                
                if (onHistoriqueFetched) {
                    onHistoriqueFetched(resultData, numSachet, isAtelierMode);
                }
                
                // Retourner aussi les refs pour les utiliser dans onOfChange
                return { data: resultData, ref_cv, ref };
            } else if (resultData?.error) {
                console.error('Erreur API:', resultData.error);
                return resultData;
            }
            return resultData;
        } catch (error) {
            console.error('Erreur lors de la récupération des détails:', error);
            return null;
        } finally {
            setLoadingOf(false);
        }
    };

    // Plus d'utilisation de listeOfs: l'historique vient uniquement de l'API sur Entrée.

    const formik = useFormik({
        initialValues: {
            joaillier: null,
            of: '',
            poids: '',
        },
        validationSchema: isOfOnly 
            ? Yup.object().shape({
                of: Yup.string().required('N° OF obligatoire.'),
            })
            : Yup.object().shape({
                of: isFormOnly ? Yup.string().nullable() : Yup.string().required('N° OF obligatoire.'),
                joaillier: Yup.object().required('Joaillier obligatoire.'),
                poids: Yup.number()
                    .typeError('Le poids doit être un nombre')
                    .positive('Le poids doit être positif')
                    .required('Poids obligatoire')
                    .nullable(),
            }),
        validateOnChange: false,
        validateOnBlur: false,
        onSubmit: (values, { resetForm }) => {
            if (isOfOnly) return;
            const newValues = { ...values, of: selectedOf, id_ligne: dataForm?.id_ligne ?? null }
            if (onSubmitForm) {
                onSubmitForm(newValues);
            }
            // Réinitialiser le formulaire SAUF le joaillier
            resetForm({
                values: {
                    joaillier: values.joaillier, // Garder le joaillier sélectionné
                    of: '',
                    poids: '',
                },
                touched: {},
                errors: {}
            });
            setHasSubmitted(false);
        },
    });

    const { values, errors, handleChange, handleSubmit, setFieldValue } = formik;

    const handleFormSubmit = (event) => {
        event.preventDefault();
        setHasSubmitted(true);
        handleSubmit(event);
    };

    // Exposer les refs au composant parent
    useImperativeHandle(ref, () => ({
        focusJoaillier: () => {
            setTimeout(() => {
                if (joaillierRef.current) {
                    joaillierRef.current.focus();
                }
            }, 100);
        },
        focusOfField: () => {
            if (ofRef.current) {
                ofRef.current.focus();
                ofRef.current.select();
            }
        },
        refetchHistorique: async (numSachetOverride) => {
            const currentOf = numSachetOverride
                || (typeof values.of === 'string' && values.of ? values.of : null)
                || ofInputValue.trim();
            if (!currentOf) {
                return null;
            }
            return fetchMovementDetails(currentOf, true);
        },
        clearAndFocusOf: () => {
            setFieldValue('of', '');
            setOfInputValue('');
            setFieldValue('poids', '');
            setHasSubmitted(false);
            if (ofRef.current) {
                setTimeout(() => {
                    if (ofRef.current) {
                        ofRef.current.focus();
                        ofRef.current.select();
                    }
                }, 100);
            }
        },
        submitForm: () => {
            setHasSubmitted(true);
            handleSubmit();
        },
    }));

    useEffect(() => {
        getListeJoaillier();
    }, [getListeJoaillier]);
    useEffect(() => {
        if (dataForm !== null) {
            populateForm(dataForm);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataForm]);

    const populateForm = (data) => {
        if (data) {
            const selectedJoaillier = listeJoaillier.find(joaillier => joaillier.id === data.joaillier);
            // On ne dépend plus de listeOfs ici; on stocke simplement la valeur d'OF si présente
            const selectedOf = data.of ? { num_sachet: data.of } : null;
            setFieldValue('joaillier', selectedJoaillier);
            setJoaillierInput(selectedJoaillier ? selectedJoaillier.label : '');
            setFieldValue('of', selectedOf);
            setFieldValue('poids', data.poids);
            if (poidsRef.current && !isFormOnly) {
                poidsRef.current.focus();
            }
        }
        else {
            formik.resetForm();
            setHasSubmitted(false);
            if (ofRef.current && !isFormOnly) {
                ofRef.current.focus();
            }
        }
    };


    useEffect(() => {
        if (values.joaillier && poidsRef.current && !isFormOnly) {
            poidsRef.current.focus();
        }
    }, [values.joaillier, isFormOnly]);

    // Notifier le parent de la validité du formulaire
    useEffect(() => {
        if (onValidityChange && !isOfOnly) {
            const isValid = values.joaillier && values.poids && !errors.joaillier && !errors.poids;
            onValidityChange(isValid);
        }
    }, [values.joaillier, values.poids, errors.joaillier, errors.poids, onValidityChange, isOfOnly]);

    return (
        <form onSubmit={handleFormSubmit}>
            <Grid container spacing={2}>
                {/* N° OF - Affiché seulement si isOfOnly ou (!isOfOnly && !isFormOnly) */}
                {!isFormOnly && (
                <Grid item xs={12}>
                    <TextField
                        label="N° OF"
                        variant="outlined"
                        fullWidth
                        value={ofInputValue}
                        onChange={(e) => {
                            setOfInputValue(e.target.value);
                            setOfError(null); // Effacer erreur à la saisie
                        }}
                        inputRef={ofRef}
                        autoFocus={!isFormOnly}
                        error={(hasSubmitted && !!errors.of) || !!ofError}
                        helperText={ofError || (hasSubmitted && errors.of ? errors.of : ' ')}
                        disabled={loadingOf}
                        onFocus={(e) => e.target.select()}
                        onClick={(e) => e.target.select()}
                        onKeyDown={async (e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                setOfError(null); // Réinitialiser l'erreur
                                const numSachet = ofInputValue.trim();
                                if (!numSachet) {
                                    setOfError('N° OF obligatoire');
                                    return;
                                }

                                setFieldValue('of', numSachet);

                                if (onOfChange) {
                                    onOfChange(numSachet, null, null, { reset: true });
                                }

                                // Décider atelier ou serti selon le contexte: par défaut atelier
                                const apiResponse = await fetchMovementDetails(numSachet, true);
                                
                                // Vérifier s'il y a une erreur API (OF n'existe pas)
                                if (apiResponse?.error) {
                                    setOfError(apiResponse.error || 'Erreur lors de la récupération de l\'OF');
                                    // Reset focus et select
                                    setTimeout(() => {
                                        if (ofRef.current) {
                                            ofRef.current.focus();
                                            ofRef.current.select();
                                        }
                                    }, 50);
                                    return;
                                }

                                // Extraire ref et refDesc du OF
                                const ref_cv = apiResponse?.ref_cv || null;
                                const ref = apiResponse?.ref || null;
                                
                                if (onOfChange) {
                                    onOfChange(numSachet, null, {
                                        num_sachet: numSachet,
                                        ref: ref_cv,
                                        refDesc: ref
                                    });
                                }
                            }
                        }}
                        InputProps={{
                            endAdornment: loadingOf ? <CircularProgress color="inherit" size={20} /> : null,
                        }}
                    />
                </Grid>
                )}

                {/* Joaillier et Poids - Affichés uniquement si !isOfOnly */}
                {!isOfOnly && (
                    <>
                        {/* Joaillier */}
                        <Grid item xs={12}>
                            <Autocomplete
                                options={listeJoaillier.filter(r => r.actif === 1)}
                                getOptionLabel={(option) => option.label}
                                value={values.joaillier}
                                onChange={(event, newValue) => {
                                    setFieldValue('joaillier', newValue);
                                    setJoaillierInput(newValue ? newValue.label : '');
                                }}
                                inputValue={joaillierInput}
                                onInputChange={(event, newInputValue) => setJoaillierInput(newInputValue || '')}
                                autoComplete
                                fullWidth
                                clearOnEscape
                                getOptionSelected={(option, values) => option.id === values.id}
                                renderInput={(params) => <TextField {...params} label="Joaillier" variant="outlined" fullWidth
                                    inputRef={joaillierRef}
                                    error={hasSubmitted && !!errors.joaillier}
                                    helperText={hasSubmitted && errors.joaillier ? errors.joaillier : ' '}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            const candidate = filteredJoailliers[0];
                                            if (candidate) {
                                                setFieldValue('joaillier', candidate);
                                                setTimeout(() => poidsRef.current?.focus(), 0);
                                            }
                                        }
                                    }} />}
                            />
                        </Grid>

                        {/* Poids */}
                        <Grid item xs={12}>
                            <TextField
                                label="Poids"
                                variant="outlined"
                                value={values.poids}
                                fullWidth
                                onChange={handleChange}
                                name="poids"
                                error={hasSubmitted && !!errors.poids}
                                helperText={hasSubmitted && errors.poids ? errors.poids : ' '}
                                inputRef={poidsRef}
                                onKeyPress={async (e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        setHasSubmitted(true);
                                        const errors = await formik.validateForm();
                                        formik.setFieldTouched('poids');
                                        if (!errors.poids && !errors.joaillier && (isFormOnly || !errors.of)) {
                                            formik.handleSubmit();
                                        }
                                    }
                                }}
                            />
                        </Grid>

                        {/* Bouton Ajouter */}
                        <Grid item xs={12} style={{ display: hideSubmitButton ? 'none' : 'flex', justifyContent: 'center' }}>
                            <Button
                                variant="contained"
                                color="primary"
                                type="submit"
                                onClick={() => setHasSubmitted(true)}
                                style={{ 
                                    textTransform: 'none',
                                    fontWeight: 500,
                                    fontSize: '0.9375rem',
                                    borderRadius: '8px',
                                    padding: '8px 24px',
                                    boxShadow: 'none'
                                }}>
                                Ajouter
                            </Button>
                        </Grid>
                    </>
                )}
            </Grid>
        </form>
    );
});

JoaillierEnvoiForm.displayName = 'JoaillierEnvoiForm';

const mapStateToProps = (state) => {
    return {
        listeJoaillier: state.generalData.listeJoaillier,
    };
};

const mapDispatchToProps = {
    getListeJoaillier,
};

export default connect(mapStateToProps, mapDispatchToProps, null, { forwardRef: true })(JoaillierEnvoiForm);
